/**
 * Condition Chart
 *
 * Displays distribution of detected customer conditions.
 */

import { motion } from 'framer-motion'

interface ConditionChartProps {
  data: Record<string, number>
}

const conditionConfig: Record<string, { color: string; label: string }> = {
  stress: { color: '#ef4444', label: 'Stress' },
  frustration: { color: '#f97316', label: 'Frustration' },
  confusion: { color: '#eab308', label: 'Confusion' },
  satisfaction: { color: '#22c55e', label: 'Satisfaction' },
  urgency: { color: '#f43f5e', label: 'Urgency' },
  anxiety: { color: '#a855f7', label: 'Anxiety' },
  impatience: { color: '#fb923c', label: 'Impatience' },
  rapport: { color: '#3b82f6', label: 'Rapport' },
  disengagement: { color: '#64748b', label: 'Disengagement' },
}

export default function ConditionChart({ data }: ConditionChartProps) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1])
  const total = entries.reduce((sum, [, count]) => sum + count, 0)

  if (entries.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-2 opacity-50">📊</div>
          <p className="text-gray-500">No conditions detected yet</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {entries.map(([condition, count], index) => {
        const config = conditionConfig[condition] || { color: '#94a3b8', label: condition }
        const percentage = total > 0 ? (count / total) * 100 : 0

        return (
          <motion.div
            key={condition}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">{config.label}</span>
              <span className="text-sm text-gray-500">
                {count} ({percentage.toFixed(0)}%)
              </span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="h-full rounded-full"
                style={{ backgroundColor: config.color }}
              />
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}
